import React, { useEffect, useState } from "react";
import API from "../api/Api";
import { Link, useNavigate } from "react-router-dom";

const ProjectList = ({ filters, onCountChange }) => {
  const [projects, setProjects] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const navigate = useNavigate();

  const isLoggedIn = () => {
    return !!localStorage.getItem("user") || !!localStorage.getItem("token");
  };

  const handleShowInterest = (projectId, alreadyInterested) => {
    if (!isLoggedIn()) {
      alert("Please login or signup to show interest");
      navigate("/registration");
      return;
    }

    if (alreadyInterested) {
      alert("You have already shown interest in this project");
      return;
    }

    navigate(`/show_intrest/${projectId}`);
  };

  const parseAmountToNumber = (value) => {
    if (!value) return 0;
    const str = value.toString().toUpperCase();

    if (str.includes("CR")) return parseFloat(str) * 10000000;
    if (str.includes("LAKH")) return parseFloat(str) * 100000;
    if (str.includes("THOUSAND")) return parseFloat(str) * 1000;

    return Number(str);
  };

  const formatAmount = (value) => {
    if (!value) return "₹0";
    const str = value.toString().toUpperCase();

    if (str.includes("CR")) return `₹${parseFloat(str).toFixed(2)} Cr`;
    if (str.includes("LAKH")) return `₹${parseFloat(str).toFixed(2)} Lakh`;
    if (str.includes("THOUSAND")) return `₹${parseFloat(str).toFixed(2)} Thousand`;

    const num = Number(str);
    if (num >= 10000000) return `₹${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `₹${(num / 100000).toFixed(2)} Lakh`;
    if (num >= 1000) return `₹${(num / 1000).toFixed(2)} Thousand`;

    return `₹${num}`;
  };

  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getDuration = (start, end) => {
    if (!start) return "—";
    if (!end) return `${formatDate(start)} - Present`;
    return `${formatDate(start)} - ${formatDate(end)}`;
  };

  const getUtilizationRate = (utilized, approved) => {
    const used = parseAmountToNumber(utilized);
    const approvedAmt = parseAmountToNumber(approved);

    if (!used || !approvedAmt) return "0%";
    return `${Math.round((used / approvedAmt) * 100)}%`;
  };

 const fetchProjects = async (filters = {}, page = 1) => {
  try {
    const params = new URLSearchParams();

    params.append("page", page);

    params.append(
      "limit",
      filters?.limit || 10
    );

    params.append(
      "type",
      filters?.status || "pending"
    );

    if (filters?.department) {
      params.append(
        "department",
        filters.department
      );
    }

    const res = await API.get(
      `/project?${params.toString()}`
    );
    if (res.data.success) {
      setProjects(res.data.data);
      setPagination(res.data.pagination);
      onCountChange(res.data.pagination.totalRecords);
    }
  } catch (err) {
    console.error(err);
  }
};

  useEffect(() => {
    fetchProjects(filters, currentPage);
  }, [filters, currentPage]);

useEffect(() => {
  setCurrentPage(1);
}, [
  filters.status,
  filters.department,
  filters.limit
]);

  return (
    <>
      <p className="text-muted small mb-3">
        Showing {projects.length} projects
      </p>

      {projects.map((p) => (
        <div className="card rounded-4 shadow-sm border mb-4" key={p.project_id}>
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h5 className="fw-semibold mb-0">{p.title}</h5>
              <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary px-3">
                {p.project_status}
              </span>
            </div>

            <small className="text-muted">
              Project ID: CSR-{String(p.project_id).padStart(5, "0")}
            </small>

            <div className="row g-4 mt-3">
              <div className="col-md-3">
                <div className="text-muted small">CSR Sector</div>
                <strong>{p.department || "—"}</strong>
              </div>

              <div className="col-md-3">
                <div className="text-muted small">Location</div>
                <strong>{p.location || "—"}</strong>
              </div>

              <div className="col-md-3">
                <div className="text-muted small">Implementing NGO</div>
                <strong>{p.executors || "Not Assigned"}</strong>
              </div>

              <div className="col-md-3">
                <div className="text-muted small">Duration</div>
                <strong>{getDuration(p.start_year, p.end_year)}</strong>
              </div>
            </div>

            <hr className="my-4" />

            <div className="row align-items-center">
              <div className="col-md-2">
                <small className="text-muted d-block">Total Estimated Cost</small>
                <strong className="text-primary">{formatAmount(p.budget)}</strong>
              </div>

              {p.project_status !== "Pending" && (
                <>
                  <div className="col-md-2">
                    <small className="text-muted d-block">Approved Amount</small>
                    <strong className="text-primary">
                      {formatAmount(p.total_funded_amount)}
                    </strong>
                  </div>

                  <div className="col-md-2">
                    <small className="text-muted d-block">Utilized Amount</small>
                    <strong className="text-success">
                      {formatAmount(p.utilized_amount)}
                    </strong>
                  </div>

                  <div className="col-md-2">
                    <small className="text-muted d-block">Utilization Rate</small>
                    <strong>
                      {getUtilizationRate(p.utilized_amount, p.total_funded_amount)}
                    </strong>
                  </div>
                </>
              )}

              <div className="col-md-4 text-end">
                <Link
                  to={`/projects/${p.project_id}`}
                  className="btn btn-primary px-4"
                >
                  View Project Details
                </Link>
              </div>

              <div className="col-md-4 show-intrest">
                {p.project_status === "Completed" ? (
                  <button className="btn btn-secondary px-4" disabled>
                    Completed
                  </button>
                ) : (
                  <button
                    className="btn btn-primary px-4"
                    disabled={p.already_interested}
                    onClick={() => handleShowInterest(p.project_id, p.already_interested)}
                  >
                    {p.already_interested ? "Interest Submitted" : "Show Interest"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
<div className="text-center mb-3 text-muted">
  Total Records : {pagination.totalRecords || 0}
  &nbsp; | &nbsp;
  Per Page : {pagination.limit || 10}
</div>
     <div className="pagination-wrapper mt-5">

  <button
    className="pagination-btn"
    disabled={currentPage === 1}
    onClick={() => setCurrentPage((prev) => prev - 1)}
  >
    <i className="fa-solid fa-chevron-left"></i>
  </button>

  {Array.from(
    { length: pagination.totalPages || 1 },
    (_, index) => (
      <button
        key={index + 1}
        className={`page-number ${
          currentPage === index + 1 ? "active" : ""
        }`}
        onClick={() => setCurrentPage(index + 1)}
      >
        {index + 1}
      </button>
    )
  )}

  <button
    className="pagination-btn"
    disabled={
      currentPage >= (pagination.totalPages || 1)
    }
    onClick={() => setCurrentPage((prev) => prev + 1)}
  >
    <i className="fa-solid fa-chevron-right"></i>
  </button>

</div>
    </>
  );
};

export default ProjectList;